import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Contract } from '../../models/Contract'
import { Type } from '../../models/Type'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import { PDFPreviewModal } from '../common/PDFPreviewModal'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { SignatureModal } from '../settings/SignatureModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    contract: Contract
    onClose: () => void
    onDelete?: () => void
    isSaving?: boolean
    isDeleting?: boolean
    onSave: (contract: Contract) => void
}

const getSignatureSourceFromContract = (contract: Contract): string | undefined => {
    if (contract.signatureData) return `data:image/png;base64,${contract.signatureData}`
    if (contract.signature) return contract.signature.url
    return undefined
}

export const ContractPage = ({
    contract,
    onClose,
    onDelete,
    isSaving,
    isDeleting,
    onSave,
}: Props) => {
    const [scratchContract, setScratchContract] = useState<Contract>(contract)
    const [showSignatureModal, setShowSignatureModal] = useState(false)

    const canSubmit = useMemo(() => {
        return scratchContract?.name && scratchContract?.memo
    }, [scratchContract])

    const hasSignature = useMemo(() => {
        return Boolean(scratchContract.signatureData || scratchContract.signature)
    }, [scratchContract])

    const [showPreview, setShowPreview] = useState(false)

    return (
        <>
            <WorkflowPage
                title="Contract"
                footerContent={
                    <div
                        className={twm(
                            'flex w-full items-center gap-x-4',
                            onDelete !== undefined ? 'justify-between' : 'justify-end'
                        )}
                    >
                        {onDelete !== undefined && (
                            <Button
                                intent="secondary"
                                className="text-danger-400"
                                onClick={onDelete}
                                disabled={isDeleting || isSaving}
                                isLoading={isDeleting}
                            >
                                Delete contract
                            </Button>
                        )}

                        <div className="flex items-center gap-x-4">
                            <Button intent="secondary" onClick={() => setShowPreview(true)}>
                                Preview
                            </Button>
                            <Button
                                intent="primary"
                                onClick={() => {
                                    onSave(scratchContract)
                                }}
                                disabled={isSaving || !canSubmit}
                                isLoading={isSaving}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                }
                onClose={onClose}
            >
                <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-4">
                    <WorkflowPage.Card title="General">
                        <WorkflowPage.IconSection
                            title="Name (required)"
                            icon={<Icon icon="pen.large" background="round" />}
                        >
                            <TextField
                                isFullWidth
                                size="medium"
                                placeholder="Enter name"
                                value={scratchContract?.name}
                                onClear={() => setScratchContract((c) => c?.clone({ name: '' }))}
                                onChange={(e) => {
                                    setScratchContract((c) => c?.clone({ name: e.target.value }))
                                }}
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Always require"
                            icon={<Icon icon="exclamation.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                // using the Type model is intentional
                                options={Type.expirationStrings().map((o) => ({
                                    id: o.id ?? '',
                                    title: o.label ?? '',
                                    subtitle: o.subtitle ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    if (option.id === 'always') {
                                        setScratchContract((c) =>
                                            c?.clone({ always_require: true, expires: undefined })
                                        )
                                    }
                                    else if (option.id === 'once') {
                                        setScratchContract((c) =>
                                            c?.clone({ always_require: false, expires: undefined })
                                        )
                                    }
                                    else {
                                        setScratchContract((c) =>
                                            c?.clone({
                                                always_require: undefined,
                                                expires: option.id,
                                            })
                                        )
                                    }
                                }}
                                selectedOptionID={scratchContract.expireString()}
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Signature"
                            description="Add your signature to appear on the contracts you send"
                            icon={<Icon icon="pen2.large" background="round" />}
                            hasHoverEffect={!hasSignature}
                            hasReducedHoverEffect={hasSignature}
                            secondaryView={
                                <Button
                                    intent="tertiaryLink"
                                    className="p-0"
                                    size="small"
                                    onClick={() => setShowSignatureModal(true)}
                                >
                                    {hasSignature ? 'Edit' : 'Add'}
                                </Button>
                            }
                        >
                            {hasSignature && (
                                <div
                                    className={clsx(
                                        'flex h-[120px] w-full flex-col items-center justify-center rounded-lg bg-gray-100 py-1',
                                        !hasSignature && 'h-full'
                                    )}
                                >
                                    <img
                                        src={getSignatureSourceFromContract(scratchContract)}
                                        alt="Signature"
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                            )}
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Contract body (required)"
                            icon={<Icon icon="note.large" background="round" />}
                        >
                            <TextArea
                                size="medium"
                                placeholder="Enter contract body"
                                value={scratchContract?.memo}
                                onClear={() => setScratchContract((c) => c?.clone({ memo: '' }))}
                                isAutoResizing
                                onChange={(e) => {
                                    setScratchContract((c) => c?.clone({ memo: e.target.value }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    </WorkflowPage.Card>
                </div>
                <SignatureModal
                    key={showSignatureModal?.toString()}
                    isOpen={showSignatureModal}
                    onClose={() => setShowSignatureModal(false)}
                    onDone={(signature) => {
                        const c = scratchContract?.clone()
                        c.signatureData = signature
                        setScratchContract(c)
                        setShowSignatureModal(false)
                    }}
                />
            </WorkflowPage>
            <PDFPreviewModal
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                endpoint={{
                    path: `/contract/print`,
                    body: scratchContract.jsonObject(),
                }}
            />
        </>
    )
}
